/**
 * *Despatch together…*'s drops table (V1 completion R5; the agreed mock-up's state 9): the command shell's custom
 * widget for `orders.record_kit_drops_despatched`. The shell keeps the carrier, the date and the dry run's notes; this
 * draws the drops of the kit that have gone, each ticked, with the tracking number of its parcel.
 *
 * - The dry run starts the table from every drop still waiting (or from the drops chosen on the Kits tab), each hinted
 *   with its place and kits. Unticking a drop sets it aside with what was typed against it; ticking it brings it back.
 * - A courier's list, pasted or uploaded (drop, tracking number, carrier), replaces the table: the dry run reads it
 *   into rows (this widget adopts them) and refuses what it cannot read.
 * - The table draws the first 20 rows; the kit can have 2,000 drops.
 *
 * Orders decides every rule; the field errors it returns land beside the rows they name.
 */
import { useEffect, useRef, useState } from "react";
import {
  despatchContext,
  dropName,
  kitsIn,
  withDropTicked,
  withTracking,
  type DespatchDropBody,
  type DropHint,
} from "@shared-core/domain/despatch-kit-drops";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { Textarea } from "../../primitives/textarea";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

/** The body paths the widget owns. */
export const DESPATCH_KIT_DROPS_PATHS = ["drops", "drops_csv"] as const;

/** Drops drawn at once; the rest are counted until the person asks to see them. */
export const DESPATCH_ROWS_SHOWN = 20;

const arrayOf = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : [];

function DespatchKitDrops({
  body,
  setValues,
  errors,
  hints,
  dryRun,
  disabled,
}: CustomWidgetProps) {
  const ticked = arrayOf<DespatchDropBody>(body.drops);
  const csv = typeof body.drops_csv === "string" ? body.drops_csv : "";
  const context = despatchContext(hints);
  const [setAside, setSetAside] = useState<Map<number, DespatchDropBody>>(
    new Map(),
  );
  const [showAll, setShowAll] = useState(false);
  const [isPasting, setIsPasting] = useState(false);
  const [pasted, setPasted] = useState("");
  const [fileProblem, setFileProblem] = useState<string | null>(null);

  // What the dry run has said of each drop, remembered so a drop set aside keeps its name.
  const known = useRef(new Map<number, DropHint>());
  for (const row of context.rows) known.current.set(row.drop, row);

  // The dry run reads a pasted or uploaded list into rows, or (asked) every drop still waiting: adopt them, and let go
  // of the text. Rows already ticked keep what was typed against them.
  const [isAddingWaiting, setIsAddingWaiting] = useState(false);
  const isReading = ticked.length === 0 && csv !== "";
  const read = dryRun?.prefill as { drops?: DespatchDropBody[] } | null;
  const adopted = useRef<unknown>(null);
  useEffect(() => {
    if (!(isReading || isAddingWaiting) || !read?.drops?.length) return;
    if (adopted.current === read) return;
    adopted.current = read;
    if (isAddingWaiting) {
      setIsAddingWaiting(false);
      const kept = new Map(setAside);
      setSetAside(new Map());
      setValues({ drops: read.drops.map((row) => kept.get(row.drop) ?? row) });
      return;
    }
    setSetAside(new Map());
    setValues({ drops: read.drops, drops_csv: undefined });
  }, [isReading, isAddingWaiting, read, setAside, setValues]);

  const errorsAt = (prefix: string) =>
    errors.filter(
      (e) => e.field === prefix || e.field.startsWith(`${prefix}.`),
    );
  const rowErrors = errors.filter((e) => /^drops\.\d+\./.test(e.field));
  const listErrors = [...errorsAt("drops_csv")].concat(
    errors.filter((e) => e.field === "drops"),
  );

  const every = [...ticked, ...setAside.values()].sort(
    (a, b) => a.drop - b.drop,
  );
  const shown = showAll ? every : every.slice(0, DESPATCH_ROWS_SHOWN);
  const tickedNumbers = new Set(ticked.map((d) => d.drop));
  const kits = kitsIn(ticked, known.current);

  const tick = (drop: number, isTicked: boolean) => {
    const next = withDropTicked(ticked, setAside, drop, isTicked);
    setSetAside(next.setAside);
    setValues({ drops: next.ticked });
  };
  const tickAll = () => {
    setSetAside(new Map());
    setValues({ drops: every });
  };
  const untickAll = () => {
    setSetAside(new Map(every.map((d) => [d.drop, d])));
    setValues({ drops: [] });
  };
  const addWaiting = () => {
    // Everything ticked is kept aside, so the rows the dry run starts from take back what was typed.
    setSetAside(new Map(every.map((d) => [d.drop, d])));
    setIsAddingWaiting(true);
    setValues({ drops: [] });
  };
  const readList = (text: string) => {
    setSetAside(new Map());
    setValues({ drops_csv: text, drops: [] });
  };
  const upload = async (file: File | undefined) => {
    if (!file) return;
    try {
      readList(await file.text());
      setFileProblem(null);
    } catch {
      setFileProblem(
        "Couldn't read that file. Check it's a CSV and try again.",
      );
    }
  };
  const indexOfTicked = (drop: number) =>
    ticked.findIndex((d) => d.drop === drop);
  const trackingIsInvalid = (drop: number) => {
    const at = indexOfTicked(drop);
    return (
      at >= 0 && errors.some((e) => e.field === `drops.${at}.tracking_number`)
    );
  };

  return (
    <section aria-labelledby="despatch-kit-drops" className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="despatch-kit-drops" className="text-sm font-semibold">
          Drops
          {context.line && (
            <span className="font-normal text-muted-foreground">
              {" "}
              {context.line}
            </span>
          )}
          <span className="font-normal text-muted-foreground">
            {" "}
            · {ticked.length.toLocaleString("en-GB")} of{" "}
            {every.length.toLocaleString("en-GB")} ticked
            {kits > 0 &&
              ` · ${kits.toLocaleString("en-GB")} ${kits === 1 ? "kit" : "kits"}`}
          </span>
        </h3>
        {(every.length > 1 || context.waitingCount > every.length) && (
          <div className="flex gap-3 text-sm">
            {context.waitingCount > every.length && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto px-0"
                disabled={disabled || isAddingWaiting}
                onClick={addWaiting}
              >
                Add the{" "}
                {(context.waitingCount - every.length).toLocaleString("en-GB")}{" "}
                other waiting
              </Button>
            )}
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto px-0"
              disabled={disabled || ticked.length === every.length}
              onClick={tickAll}
            >
              Tick all
            </Button>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto px-0"
              disabled={disabled || ticked.length === 0}
              onClick={untickAll}
            >
              Untick all
            </Button>
          </div>
        )}
      </div>

      {every.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th scope="col" className="w-8 py-1 pr-2 font-normal">
                  <span className="sr-only">Include</span>
                </th>
                <th scope="col" className="py-1 pr-3 font-normal">
                  Drop
                </th>
                <th scope="col" className="py-1 pr-3 text-right font-normal">
                  Kits
                </th>
                <th scope="col" className="py-1 font-normal">
                  Tracking number
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const hint = known.current.get(row.drop);
                const name = dropName(row.drop, hint);
                const isTicked = tickedNumbers.has(row.drop);
                return (
                  <tr
                    key={row.drop}
                    className="border-t border-border align-middle"
                  >
                    <td className="py-1.5 pr-2">
                      <Checkbox
                        aria-label={`Include ${name}`}
                        checked={isTicked}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          tick(row.drop, checked === true)
                        }
                      />
                    </td>
                    <td
                      className={
                        isTicked
                          ? "py-1.5 pr-3"
                          : "py-1.5 pr-3 text-muted-foreground"
                      }
                    >
                      {name}
                      {hint?.postcode && (
                        <span className="text-xs text-muted-foreground">
                          {" "}
                          · {hint.postcode}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {hint?.kits ?? ""}
                    </td>
                    <td className="py-1.5">
                      <Input
                        type="text"
                        className="w-44"
                        aria-label={`Tracking number: ${name}`}
                        aria-invalid={trackingIsInvalid(row.drop) || undefined}
                        disabled={disabled || !isTicked}
                        value={row.tracking_number ?? ""}
                        onChange={(e) =>
                          setValues(
                            {
                              drops: withTracking(
                                ticked,
                                row.drop,
                                e.target.value,
                              ),
                            },
                            { debounce: true },
                          )
                        }
                      />
                    </td>
                  </tr>
                );
              })}
              {every.length > shown.length && (
                <tr className="border-t border-border">
                  <td
                    colSpan={4}
                    className="py-1.5 text-xs text-muted-foreground"
                  >
                    … {(every.length - shown.length).toLocaleString("en-GB")}{" "}
                    more{" "}
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto px-0 text-xs"
                      onClick={() => setShowAll(true)}
                    >
                      Show all
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <p role="status" className="text-sm text-muted-foreground">
          {isReading || dryRun === null
            ? "Reading the drops…"
            : "No drops ticked."}
        </p>
      )}

      <div className="space-y-2 rounded-md border border-dashed border-border bg-muted/30 p-3">
        <p className="text-sm">Or paste or upload the courier&apos;s list.</p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled || isReading}
            onClick={() => setIsPasting(!isPasting)}
          >
            Paste…
          </Button>
          <Label
            className="inline-flex h-8 cursor-pointer items-center rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-accent"
            htmlFor="despatch-kit-drops-upload"
          >
            Upload CSV…
          </Label>
          <input
            id="despatch-kit-drops-upload"
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            disabled={disabled || isReading}
            onChange={(e) => void upload(e.target.files?.[0])}
          />
        </div>
        {isPasting && (
          <div className="space-y-2">
            <Label htmlFor="despatch-kit-drops-paste">
              Paste the courier&apos;s list
            </Label>
            <Textarea
              id="despatch-kit-drops-paste"
              rows={5}
              placeholder="Drop, tracking number, carrier…"
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
            />
            <Button
              type="button"
              size="sm"
              disabled={disabled || pasted.trim() === ""}
              onClick={() => {
                readList(pasted);
                setIsPasting(false);
              }}
            >
              Read the list
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Columns: drop (its number), tracking number, carrier. It replaces the
          drops ticked above.
        </p>
        {fileProblem && (
          <p role="alert" className="text-sm text-red-600">
            {fileProblem}
          </p>
        )}
      </div>

      {rowErrors.map((e) => (
        <p
          key={`${e.field}-${e.message}`}
          role="alert"
          className="text-sm text-red-600"
        >
          {e.message}
        </p>
      ))}
      {listErrors.map((e) => (
        <p
          key={`${e.field}-${e.message}`}
          role="alert"
          className="text-sm text-red-600"
        >
          {e.message}
        </p>
      ))}
    </section>
  );
}

export const despatchKitDropsWidget: CustomWidget = {
  ownedPaths: DESPATCH_KIT_DROPS_PATHS,
  Component: DespatchKitDrops,
};
